
import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon, Pencil, Trash2, ArrowUpDown, Eye } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";

type Testimonial = Tables<"testimonials">;

export type SortField = 'author_name' | 'company_name' | 'role_title' | 'industry' | 'service_category' | 'rating' | 'approved' | 'created_at';
export type SortDirection = 'asc' | 'desc' | null;

interface TestimonialTableProps {
  testimonials: Testimonial[];
  isLoading: boolean;
  onApprove: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (testimonial: Testimonial) => void;
  onPreview?: (testimonial: Testimonial) => void;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
}

const TestimonialTable = ({
  testimonials,
  isLoading,
  onApprove,
  onDelete,
  onEdit,
  onPreview,
  sortField,
  sortDirection,
  onSort,
}: TestimonialTableProps) => {
  
  const handleSort = (field: SortField) => {
    if (onSort) {
      onSort(field);
    }
  };
  
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Author</TableHead>
                <TableHead>Role & Company</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-24" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead sortable sortDirection={sortField === 'author_name' ? sortDirection : null} onSort={() => handleSort('author_name')}>Author</TableHead>
              <TableHead>Role & Company</TableHead>
              <TableHead sortable sortDirection={sortField === 'industry' ? sortDirection : null} onSort={() => handleSort('industry')}>Industry</TableHead>
              <TableHead sortable sortDirection={sortField === 'service_category' ? sortDirection : null} onSort={() => handleSort('service_category')}>Service</TableHead>
              <TableHead sortable sortDirection={sortField === 'rating' ? sortDirection : null} onSort={() => handleSort('rating')}>Rating</TableHead>
              <TableHead sortable sortDirection={sortField === 'approved' ? sortDirection : null} onSort={() => handleSort('approved')}>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center">
                <div className="flex flex-col items-center justify-center py-4">
                  <p className="text-muted-foreground">No testimonials found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add testimonials to showcase customer feedback
                  </p>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sortable sortDirection={sortField === 'author_name' ? sortDirection : null} onSort={() => handleSort('author_name')}>Author</TableHead>
            <TableHead>Role & Company</TableHead>
            <TableHead sortable sortDirection={sortField === 'industry' ? sortDirection : null} onSort={() => handleSort('industry')}>Industry</TableHead>
            <TableHead sortable sortDirection={sortField === 'service_category' ? sortDirection : null} onSort={() => handleSort('service_category')}>Service</TableHead>
            <TableHead sortable sortDirection={sortField === 'rating' ? sortDirection : null} onSort={() => handleSort('rating')}>Rating</TableHead>
            <TableHead sortable sortDirection={sortField === 'approved' ? sortDirection : null} onSort={() => handleSort('approved')}>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {testimonials.map((testimonial) => (
            <TableRow key={testimonial.id}>
              <TableCell className="font-medium">{testimonial.author_name || "Anonymous"}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  {testimonial.role_title && <span className="text-sm">{testimonial.role_title}</span>}
                  {testimonial.company_name && <span className="text-xs text-muted-foreground">{testimonial.company_name}</span>}
                </div>
              </TableCell>
              <TableCell>{testimonial.industry}</TableCell>
              <TableCell>{testimonial.service_category}</TableCell>
              <TableCell>
                {testimonial.rating ? (
                  <span className="text-sm text-yellow-500">
                    {'★'.repeat(testimonial.rating)}
                    {'☆'.repeat(5 - testimonial.rating)}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">No rating</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={testimonial.approved ? "default" : "outline"}>
                  {testimonial.approved ? "Approved" : "Pending"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {!testimonial.approved && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => onApprove(testimonial.id)}
                      className="h-8 w-8 p-0"
                    >
                      <CheckIcon className="h-4 w-4" />
                      <span className="sr-only">Approve</span>
                    </Button>
                  )}
                  {onPreview && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => onPreview(testimonial)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">Preview</span>
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onEdit(testimonial)}
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => onDelete(testimonial.id)}
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default TestimonialTable;
